import React, { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImagePicker from "./ImagePicker";

const getPath = (obj, path) => path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
const setPath = (obj, path, value) => {
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) {
    cur[path[i]] = { ...(cur[path[i]] || {}) };
    cur = cur[path[i]];
  }
  cur[path[path.length - 1]] = value;
  return next;
};

function Field({ label, value, onChange, multiline = false, testid }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-600 mb-1.5 block">{label}</label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid}
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300 resize-y" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" />
      )}
    </div>
  );
}

function Section({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <span className="font-bold text-slate-800 text-sm">{title}</span>
        <span className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && <div className="px-5 pb-5 flex flex-col gap-4 border-t border-slate-100 pt-4">{children}</div>}
    </div>
  );
}

// Editor genérico de listas (FAQ, avaliações, etapas, cards)
function ListEditor({ items, onChange, fields, addLabel, emptyItem, testidPrefix }) {
  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-3" data-testid={testidPrefix}>
      {items.map((item, i) => (
        <div key={i} className="bg-slate-50 rounded-xl p-4 flex flex-col gap-3 relative">
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} aria-label="Remover item"
            data-testid={`${testidPrefix}-remove-${i}`}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white text-slate-400 hover:text-red-500 flex items-center justify-center shadow-soft">
            <Trash2 size={13} />
          </button>
          {fields.map((f) =>
            f.type === "select" ? (
              <div key={f.key}>
                <label className="text-xs font-bold text-slate-600 mb-1.5 block">{f.label}</label>
                <Select value={item[f.key]} onValueChange={(v) => update(i, f.key, v)}>
                  <SelectTrigger className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <Field key={f.key} label={f.label} value={item[f.key]} multiline={f.multiline}
                onChange={(v) => update(i, f.key, f.type === "number" ? Number(v) : v)} testid={`${testidPrefix}-${f.key}-${i}`} />
            )
          )}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { ...emptyItem }])} data-testid={`${testidPrefix}-add`}
        className="self-start inline-flex items-center gap-1.5 text-xs font-bold text-pink-600 bg-pink-50 hover:bg-pink-100 px-4 py-2.5 rounded-full transition-colors">
        <Plus size={14} /> {addLabel}
      </button>
    </div>
  );
}

const ICON_OPTIONS = [
  { value: "printer", label: "Impressora 3D" },
  { value: "palette", label: "Paleta criativa" },
  { value: "sparkles", label: "Brilho/Acabamento" },
  { value: "heart", label: "Coração" },
  { value: "star", label: "Estrela" },
  { value: "package", label: "Pacote" },
];

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setContent(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!content) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-pink-500" size={28} /></div>;
  }

  const set = (path, value) => setContent((prev) => setPath(prev, path, value));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Conteúdo do site atualizado!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const moveSection = (i, dir) => {
    const sections = [...(content.sections || [])];
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    [sections[i], sections[j]] = [sections[j], sections[i]];
    set(["sections"], sections);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="admin-content-tab">
      <div className="sticky top-16 z-10 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="content-save-btn"
          className="bg-brand-gradient text-white text-sm font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 shadow-soft active:scale-95 transition-transform disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Salvando..." : "Salvar alterações"}
        </button>
      </div>

      <Section title="🏪 Geral (marca, logo e contato)">
        <Field label="Nome da loja" value={content.brand_name} onChange={(v) => set(["brand_name"], v)} testid="content-brand-name" />
        <Field label="Slogan (rodapé)" value={content.tagline} onChange={(v) => set(["tagline"], v)} testid="content-tagline" />
        <ImagePicker images={content.logo_url} multiple={false} onChange={(v) => set(["logo_url"], v)} label="Logo (substitui o mascote padrão)" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="WhatsApp (só números, com DDI)" value={content.contact?.whatsapp} onChange={(v) => set(["contact", "whatsapp"], v)} testid="content-whatsapp" />
          <Field label="WhatsApp (exibição)" value={content.contact?.whatsapp_display} onChange={(v) => set(["contact", "whatsapp_display"], v)} testid="content-whatsapp-display" />
          <Field label="Instagram (@)" value={content.contact?.instagram} onChange={(v) => set(["contact", "instagram"], v)} testid="content-instagram" />
          <Field label="Link do Instagram" value={content.contact?.instagram_url} onChange={(v) => set(["contact", "instagram_url"], v)} testid="content-instagram-url" />
          <Field label="Localização" value={content.contact?.location} onChange={(v) => set(["contact", "location"], v)} testid="content-location" />
        </div>
      </Section>

      <Section title="🖼️ Ordem e visibilidade das seções">
        <div className="flex flex-col gap-2">
          {(content.sections || []).map((s, i) => (
            <div key={s.key} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`section-row-${s.key}`}>
              <div className="flex flex-col">
                <button onClick={() => moveSection(i, -1)} disabled={i === 0} aria-label="Subir" data-testid={`section-up-${s.key}`} className="text-slate-400 hover:text-pink-500 disabled:opacity-30"><ArrowUp size={13} /></button>
                <button onClick={() => moveSection(i, 1)} disabled={i === content.sections.length - 1} aria-label="Descer" data-testid={`section-down-${s.key}`} className="text-slate-400 hover:text-pink-500 disabled:opacity-30"><ArrowDown size={13} /></button>
              </div>
              <span className="flex-1 text-sm font-semibold text-slate-700">{s.label}</span>
              <Switch checked={s.enabled} onCheckedChange={(v) => {
                const sections = content.sections.map((x, idx) => (idx === i ? { ...x, enabled: v } : x));
                set(["sections"], sections);
              }} data-testid={`section-toggle-${s.key}`} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="🌟 Banner principal (hero)">
        <Field label="Selo acima do título" value={content.hero?.badge} onChange={(v) => set(["hero", "badge"], v)} testid="content-hero-badge" />
        <Field label="Título" value={content.hero?.title} onChange={(v) => set(["hero", "title"], v)} testid="content-hero-title" />
        <Field label="Subtítulo" value={content.hero?.subtitle} onChange={(v) => set(["hero", "subtitle"], v)} multiline testid="content-hero-subtitle" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Botão principal" value={content.hero?.cta_primary} onChange={(v) => set(["hero", "cta_primary"], v)} testid="content-hero-cta1" />
          <Field label="Botão WhatsApp" value={content.hero?.cta_secondary} onChange={(v) => set(["hero", "cta_secondary"], v)} testid="content-hero-cta2" />
        </div>
        <ImagePicker images={content.hero?.images || []} onChange={(v) => set(["hero", "images"], v)} label="Fotos do banner (até 3 aparecem flutuando)" />
      </Section>

      <Section title="🗂️ Títulos das vitrines">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Categorias — título" value={content.categories_section?.title} onChange={(v) => set(["categories_section", "title"], v)} testid="content-cat-title" />
          <Field label="Categorias — subtítulo" value={content.categories_section?.subtitle} onChange={(v) => set(["categories_section", "subtitle"], v)} testid="content-cat-subtitle" />
          <Field label="Destaques — título" value={content.featured_section?.title} onChange={(v) => set(["featured_section", "title"], v)} testid="content-featured-title" />
          <Field label="Destaques — subtítulo" value={content.featured_section?.subtitle} onChange={(v) => set(["featured_section", "subtitle"], v)} testid="content-featured-subtitle" />
          <Field label="Queridinhos — título" value={content.best_sellers_section?.title} onChange={(v) => set(["best_sellers_section", "title"], v)} testid="content-best-title" />
          <Field label="Queridinhos — subtítulo" value={content.best_sellers_section?.subtitle} onChange={(v) => set(["best_sellers_section", "subtitle"], v)} testid="content-best-subtitle" />
          <Field label="Novidades — título" value={content.new_arrivals_section?.title} onChange={(v) => set(["new_arrivals_section", "title"], v)} testid="content-new-title" />
          <Field label="Novidades — subtítulo" value={content.new_arrivals_section?.subtitle} onChange={(v) => set(["new_arrivals_section", "subtitle"], v)} testid="content-new-subtitle" />
        </div>
      </Section>

      <Section title="💜 Por que escolher a Art3D">
        <Field label="Título da seção" value={content.why_choose?.title} onChange={(v) => set(["why_choose", "title"], v)} testid="content-why-title" />
        <ListEditor items={content.why_choose?.items || []} onChange={(v) => set(["why_choose", "items"], v)}
          fields={[
            { key: "title", label: "Título" },
            { key: "text", label: "Texto", multiline: true },
            { key: "icon", label: "Ícone", type: "select", options: ICON_OPTIONS },
          ]}
          addLabel="Adicionar card" emptyItem={{ icon: "sparkles", title: "", text: "" }} testidPrefix="why-items" />
      </Section>

      <Section title="💗 Sobre a Art3D">
        <Field label="Título" value={content.about?.title} onChange={(v) => set(["about", "title"], v)} testid="content-about-title" />
        <Field label="Texto" value={content.about?.text} onChange={(v) => set(["about", "text"], v)} multiline testid="content-about-text" />
        <ImagePicker images={content.about?.image} multiple={false} onChange={(v) => set(["about", "image"], v)} label="Foto/ilustração da marca" />
      </Section>

      <Section title="🛒 Como comprar (3 etapas)">
        <Field label="Título da seção" value={content.how_to_buy?.title} onChange={(v) => set(["how_to_buy", "title"], v)} testid="content-how-title" />
        <ListEditor items={content.how_to_buy?.steps || []} onChange={(v) => set(["how_to_buy", "steps"], v)}
          fields={[{ key: "title", label: "Título" }, { key: "text", label: "Texto", multiline: true }]}
          addLabel="Adicionar etapa" emptyItem={{ title: "", text: "" }} testidPrefix="how-steps" />
      </Section>

      <Section title="📦 Como funciona seu pedido">
        <Field label="Título da seção" value={content.order_steps?.title} onChange={(v) => set(["order_steps", "title"], v)} testid="content-order-title" />
        <ListEditor items={content.order_steps?.steps || []} onChange={(v) => set(["order_steps", "steps"], v)}
          fields={[{ key: "title", label: "Título" }, { key: "text", label: "Texto", multiline: true }]}
          addLabel="Adicionar etapa" emptyItem={{ title: "", text: "" }} testidPrefix="order-steps" />
      </Section>

      <Section title="📍 Entrega e retirada">
        <Field label="Título" value={content.delivery?.title} onChange={(v) => set(["delivery", "title"], v)} testid="content-delivery-title" />
        <Field label="Destaque (ex.: Entregamos em Natal e região)" value={content.delivery?.subtitle} onChange={(v) => set(["delivery", "subtitle"], v)} testid="content-delivery-subtitle" />
        <Field label="Regiões atendidas" value={content.delivery?.regions} onChange={(v) => set(["delivery", "regions"], v)} multiline testid="content-delivery-regions" />
        <Field label="Formas de entrega" value={content.delivery?.methods} onChange={(v) => set(["delivery", "methods"], v)} multiline testid="content-delivery-methods" />
        <Field label="Prazo" value={content.delivery?.deadline} onChange={(v) => set(["delivery", "deadline"], v)} testid="content-delivery-deadline" />
        <Field label="Taxa de entrega" value={content.delivery?.fee} onChange={(v) => set(["delivery", "fee"], v)} testid="content-delivery-fee" />
      </Section>

      <Section title="⭐ Avaliações de clientes">
        <Field label="Título da seção" value={content.testimonials?.title} onChange={(v) => set(["testimonials", "title"], v)} testid="content-testimonials-title" />
        <p className="text-[11px] text-slate-400">Adicione apenas avaliações reais dos seus clientes.</p>
        <ListEditor items={content.testimonials?.items || []} onChange={(v) => set(["testimonials", "items"], v)}
          fields={[
            { key: "name", label: "Nome do cliente" },
            { key: "text", label: "Avaliação", multiline: true },
            { key: "rating", label: "Nota (1-5)", type: "number" },
          ]}
          addLabel="Adicionar avaliação" emptyItem={{ name: "", text: "", rating: 5 }} testidPrefix="testimonial-items" />
      </Section>

      <Section title="🖼️ Galeria">
        <Field label="Título" value={content.gallery?.title} onChange={(v) => set(["gallery", "title"], v)} testid="content-gallery-title" />
        <Field label="Subtítulo" value={content.gallery?.subtitle} onChange={(v) => set(["gallery", "subtitle"], v)} testid="content-gallery-subtitle" />
        <ImagePicker images={content.gallery?.images || []} onChange={(v) => set(["gallery", "images"], v)} label="Fotos da galeria" />
      </Section>

      <Section title="📸 Instagram">
        <Field label="Título" value={content.instagram?.title} onChange={(v) => set(["instagram", "title"], v)} testid="content-insta-title" />
        <Field label="@ do perfil" value={content.instagram?.handle} onChange={(v) => set(["instagram", "handle"], v)} testid="content-insta-handle" />
        <Field label="Texto do botão" value={content.instagram?.cta} onChange={(v) => set(["instagram", "cta"], v)} testid="content-insta-cta" />
        <ImagePicker images={content.instagram?.images || []} onChange={(v) => set(["instagram", "images"], v)} label="Fotos/posts" />
      </Section>

      <Section title="🎁 Banner de presentes">
        <Field label="Título" value={content.gift_banner?.title} onChange={(v) => set(["gift_banner", "title"], v)} testid="content-gift-title" />
        <Field label="Texto" value={content.gift_banner?.text} onChange={(v) => set(["gift_banner", "text"], v)} multiline testid="content-gift-text" />
        <Field label="Texto do botão" value={content.gift_banner?.cta} onChange={(v) => set(["gift_banner", "cta"], v)} testid="content-gift-cta" />
      </Section>

      <Section title="❓ Dúvidas frequentes (FAQ)">
        <Field label="Título da seção" value={content.faq?.title} onChange={(v) => set(["faq", "title"], v)} testid="content-faq-title" />
        <ListEditor items={content.faq?.items || []} onChange={(v) => set(["faq", "items"], v)}
          fields={[{ key: "q", label: "Pergunta" }, { key: "a", label: "Resposta", multiline: true }]}
          addLabel="Adicionar pergunta" emptyItem={{ q: "", a: "" }} testidPrefix="faq-items" />
      </Section>

      <Section title="💬 Chamada do WhatsApp">
        <Field label="Título" value={content.whatsapp_cta?.title} onChange={(v) => set(["whatsapp_cta", "title"], v)} testid="content-wpp-title" />
        <Field label="Texto" value={content.whatsapp_cta?.text} onChange={(v) => set(["whatsapp_cta", "text"], v)} testid="content-wpp-text" />
        <Field label="Texto do botão" value={content.whatsapp_cta?.cta} onChange={(v) => set(["whatsapp_cta", "cta"], v)} testid="content-wpp-cta" />
      </Section>
    </div>
  );
}
